import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { workspaceClient } from '@/services/workspace-client';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

/** Query keys for the assignment editor. */
const areaKeys = {
  areas: (unitId: string) => ['workspace', 'areas', unitId] as const,
  assignments: (unitId: string) => ['workspace', 'area-assignments', unitId] as const,
  members: (unitId: string) => ['workspace', 'members', unitId] as const,
};

/**
 * Admin editor assigning members to mission areas: each approved member gets a
 * row of area pills; toggling assigns/unassigns them. Areas come from the
 * drawn mission areas; members from the unit roster.
 */
export function AreaAssignEditor({ unitId }: { unitId: string }): React.JSX.Element {
  const queryClient = useQueryClient();

  const areas = useQuery({ queryKey: areaKeys.areas(unitId), queryFn: () => workspaceClient.listAreas(unitId) });
  const members = useQuery({
    queryKey: areaKeys.members(unitId),
    queryFn: () => workspaceClient.getMembers(unitId, 'approved'),
  });
  const assignments = useQuery({
    queryKey: areaKeys.assignments(unitId),
    queryFn: () => workspaceClient.listAreaAssignments(unitId),
  });

  const assignedSet = useMemo(
    () => new Set((assignments.data ?? []).map((a) => `${a.areaId}:${a.sub}`)),
    [assignments.data],
  );

  const toggle = useMutation({
    mutationFn: async ({ areaId, memberSub, on }: { areaId: string; memberSub: string; on: boolean }) =>
      on
        ? workspaceClient.unassignArea(unitId, areaId, memberSub)
        : workspaceClient.assignArea(unitId, areaId, memberSub),
    onSettled: () => queryClient.invalidateQueries({ queryKey: areaKeys.assignments(unitId) }),
  });

  const areaList = areas.data ?? [];
  const memberList = members.data ?? [];

  if (areaList.length === 0) {
    return (
      <p className="p-3 text-xs text-muted-foreground">
        No mission areas yet — draw one in Tools and save it as a unit area.
      </p>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-3 p-3">
        <p className="text-[11px] text-muted-foreground/80">
          Set members up with the mission areas they're responsible for.
        </p>
        {memberList.map((member) => (
          <div key={member.id} className="rounded-md bg-white/[0.03] p-2">
            <p className="mb-1.5 truncate text-xs font-medium text-foreground">{member.keycloakSub}</p>
            <div className="flex flex-wrap gap-1">
              {areaList.map((area) => {
                const on = assignedSet.has(`${area.id}:${member.keycloakSub}`);
                return (
                  <button
                    key={area.id}
                    type="button"
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate({ areaId: area.id, memberSub: member.keycloakSub, on })}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-[11px] transition-colors disabled:opacity-50',
                      on
                        ? 'border-echo-yellow/50 bg-echo-yellow/15 text-echo-yellow'
                        : 'border-echo-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {area.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {memberList.length === 0 && !members.isLoading && (
          <p className="text-xs text-muted-foreground">No approved members.</p>
        )}
      </div>
    </ScrollArea>
  );
}
